import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import {
  FcEmployerListItemDto,
  FcModifyEmployerListItemDto,
  FormGroupValidationRules,
} from 'webcommon/shared';
import {
  FcGenericItemModalBaseComponent,
  FcListValidationService,
  FormGroupArray
} from '../../generic';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-employer-list-modal',
  templateUrl: './fc-employer-list-modal.component.html',
})
export class FcEmployerListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  public override baseId = 'fc-employer-item';
  public override isSubmitted = false;

  public notesControl: FormControl = new FormControl('');
  public inactiveControl: FormControl = new FormControl(false);

  public contactInfoGroup: FormGroup = new FormGroup({
    notes: this.notesControl,
    inactive: this.inactiveControl
  });
  public workersCompFormGroup: FormGroup = new FormGroup({});
  public form: FormGroup = new FormGroup({
    contactInfoGroup: this.contactInfoGroup,
    workersCompFormGroup: this.workersCompFormGroup,
  });

  public listItem: FcEmployerListItemDto = {
    Id: '',
    CorporationUid: '',
    Corporation: {
      Id: '',
      Name: '',
      Inactive: false,
      Address: {
        ZipUid: '',
        CountyName: '',
        CountyCode: '',
        Address1: '',
        Address2: '',
        City: '',
        State: '',
        Zip: '',
        Country: '',
      },
      Phone1: {
        PhoneNumber: '',
        PhoneType: '',
        PhoneTypeUid: '',
      },
      Phone2: {
        PhoneNumber: '',
        PhoneType: '',
        PhoneTypeUid: '',
      },
      Phone3: {
        PhoneNumber: '',
        PhoneType: '',
        PhoneTypeUid: '',
      },
      Phone4: {
        PhoneNumber: '',
        PhoneType: '',
        PhoneTypeUid: '',
      },
      PrimaryContact: '',
      SecondaryContact: '',
      Email1: '',
      Email2: '',
    },
    Notes: '',
    RelEmployerInsurancePlan: [],
    UserDefined: false,
    Inactive: false,
  };

  public listItemDefaultValidation: FormGroupValidationRules = {
    ExternalID: {
      MaxLength: 25,
    },
    Name: {
      IsRequired: true,
      MaxLength: 100,
    },
    Notes: {
      IsRequired: false,
      MaxLength: 65535,
    }
  };

  override ngOnInit() {
    super.ngOnInit();
    this.loadListItem(this.listItemDefaultValidation);
  }

  setForm(): void {
    // Notes Control
    this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
    this.notesControl.setValue(this.listItem.Notes);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem.Inactive);

    this.validateForm();
  }

  public getFormValidationErrors(formGroup: FormGroupArray): number {
    return FcListValidationService.getFormValidationErrors(formGroup, this.isSubmitted);
  }

  public override save(): void {
    const values = this.form.getRawValue();
    this.isSubmitted = true;
    const workerCompFormArray = values.workersCompFormGroup.WorkerCompFormArray;
    const contactInfo = values.contactInfoGroup;
    const mappedArray = workerCompFormArray?.map((el: {
      EffectiveDate: string;
      ExpirationDate: string;
      InsurancePlan: string[];
      InsuranceContact: string[];
      RelEmployerInsurancePlanUid: string;
    }) => {
      return {
        EffectiveDate: el.EffectiveDate,
        ...(el.ExpirationDate && { ExpirationDate: el.ExpirationDate}),
        InsurancePlanUid: this.parseFindControlValue(el.InsurancePlan),
        InsuranceContactUid: this.parseFindControlValue(el.InsuranceContact),
        ...(el.RelEmployerInsurancePlanUid && { RelEmployerInsurancePlanUid: el.RelEmployerInsurancePlanUid })
      };
    });

    const modifyItemDto: FcModifyEmployerListItemDto = {
      Corporation: {
        Id: values.workersCompFormGroup.CorporationUid,
        Name: values.contactInfoGroup.name,
        Address: {
          CountyName: values.contactInfoGroup.address?.countyName,
          CountyCode: values.contactInfoGroup.address?.countyCode,
          Address1: values.contactInfoGroup.address?.addressOne,
          Address2: values.contactInfoGroup.address?.addressTwo,
          City: values.contactInfoGroup.address?.city,
          State: values.contactInfoGroup.address?.state,
          Zip: values.contactInfoGroup.address?.zip,
          Country: values.contactInfoGroup.address?.country,
        },
        PrimaryContact: values.contactInfoGroup.primaryContact,
        SecondaryContact: values.contactInfoGroup.secondaryContact,
        Email1: values.contactInfoGroup.emailOne,
        Email2: values.contactInfoGroup.emailTwo,
        ExternalID: values.contactInfoGroup.externalId,
      },
      Notes: values.contactInfoGroup.notes,
      Inactive: values.contactInfoGroup.inactive,
      RelEmployerInsurancePlan: mappedArray
    };

    if (
      contactInfo.phoneOne?.phoneNumber &&
      contactInfo.phoneOne?.phoneType &&
      contactInfo.phoneOne?.phoneTypeUid &&
      modifyItemDto.Corporation != null
    ) {
      modifyItemDto.Corporation.Phone1 = {
        PhoneNumber: contactInfo.phoneOne.phoneNumber,
        PhoneType: contactInfo.phoneOne.phoneType,
        PhoneTypeUid: this.parseFindControlValue(contactInfo.phoneOne.phoneTypeUid),
      };
    }
    if (
      contactInfo.phoneTwo?.phoneNumber &&
      contactInfo.phoneTwo?.phoneType &&
      contactInfo.phoneTwo?.phoneTypeUid &&
      modifyItemDto.Corporation != null
    ) {
      modifyItemDto.Corporation.Phone2 = {
        PhoneNumber: contactInfo.phoneTwo.phoneNumber,
        PhoneType: contactInfo.phoneTwo.phoneType,
        PhoneTypeUid: this.parseFindControlValue(contactInfo.phoneTwo.phoneTypeUid),
      };
    }
    if (
      contactInfo.phoneThree?.phoneNumber &&
      contactInfo.phoneThree?.phoneType &&
      contactInfo.phoneThree?.phoneTypeUid &&
      modifyItemDto.Corporation != null
    ) {
      modifyItemDto.Corporation.Phone3 = {
        PhoneNumber: contactInfo.phoneThree.phoneNumber,
        PhoneType: contactInfo.phoneThree.phoneType,
        PhoneTypeUid: this.parseFindControlValue(contactInfo.phoneThree.phoneTypeUid),
      };
    }
    if (
      contactInfo.phoneFour?.phoneNumber &&
      contactInfo.phoneFour?.phoneType &&
      contactInfo.phoneFour?.phoneTypeUid &&
      modifyItemDto.Corporation != null
    ) {
      modifyItemDto.Corporation.Phone4 = {
        PhoneNumber: contactInfo.phoneFour.phoneNumber,
        PhoneType: contactInfo.phoneFour.phoneType,
        PhoneTypeUid: this.parseFindControlValue(contactInfo.phoneFour.phoneTypeUid),
      };
    }

    super.save<FcModifyEmployerListItemDto>(modifyItemDto);
  }
}
